import React from 'react'

import classNames from 'clsx'
import PropTypes from 'prop-types'
import { omit, pick } from 'ramda'
import { useConnect } from 'redux-bundler-hook'

import { CircularProgress } from '@mui/material'
import { makeStyles } from '@mui/styles'

import createLogger from '~/src/Lib/Logging'
import { idAndNameShape } from '~/src/Lib/PropTypes'
import { shallowEqualsMemoizer, useClientRect } from '~/src/Lib/Utils'

import Calendar from './Calendar'
import { SCOPING_PROP_DEFAULT_VALUE } from './constants'
import Filters from './Filters'
import List from './List'
import useJournalState, { JournalContext } from './utils'

export * from './utils'
export { default as useJournalState } from './utils'

const displayName = 'Journal'
const logger = createLogger(displayName)

const calendarStatePicker = pick([
  'calendarStartDate',
  'calendarEndDate',
  'journal',
  'loading',
  'selectedDate',
  'today',
  'dispatch',
  'goToNextMonth',
  'goToPrevMonth',
])
const calendarPropsPicker = pick(['breakpoint'])
const filterStatePicker = pick(['activeTypes', 'activeUser', 'allowedTypes', 'dispatch'])
const filterPropsPicker = pick(['activeTypes', 'activeUser', 'allowedTypes', 'onFilterChange'])
const getFilterProps = (props, state) => {
  const { activeTypes: propsTypes, activeUser: propsUser, allowedTypes: propsAllowedTypes, ...filterProps } = filterPropsPicker(props)
  const { activeTypes: stateTypes, activeUser: stateUser, allowedTypes: stateAllowedTypes, ...filterState } = filterStatePicker(state)
  const activeTypes = propsTypes ?? stateTypes
  const activeUser = typeof propsUser === 'symbol' ? stateUser : propsUser
  const allowedTypes = propsAllowedTypes ?? stateAllowedTypes

  return {
    ...filterProps,
    ...filterState,
    allowedTypes,
    activeTypes,
    activeUser
  }
}
const omitNonListState = omit([
  'calendarEndDate',
  'calendarStartDate',
  'infiniteLoading',
  'loadMore',
  'previousInfiniteloading',
  'scrollerIndices',
  'selectedDate',
  'topItem',
])

const useStyles = makeStyles(theme => ({
  container: {
    position: 'relative',
    display: 'grid',
    height: '100%',
    gridTemplateAreas: [
      '"journalCalendar"',
      '"journalFilters"',
      '"journalList"'
    ].join('\n'),
    gridTemplateRows: 'auto auto 1fr',
    gridTemplateColumns: '100%',
    // [theme.breakpoints.only('sm')]: {
    //   gridTemplateAreas: '"journalCalendar journalList"',
    //   gridTemplateColumns: '50% 50%',
    //   gridTemplateRows: '100%',
    // },
    '&$noCalendar': {
      gridTemplateAreas: [
        '"journalFilters"',
        '"journalList"'
      ].join('\n'),
      gridTemplateRows: 'auto 1fr',
    }
  },
  calendarContainer: {
    gridArea: 'journalCalendar',
    width: '100%',
    backgroundColor: theme.palette.background.dark,
    transform: 'translate3d(0, 0, 0)',
  },
  noCalendar: {},
  filters: {
    gridArea: 'journalFilters',
    '[class^=MuiDrawer] &': {
      paddingLeft: '1rem',
    },
  },
  journal: {
    gridArea: 'journalList',
    textAlign: 'center',
    minWidth: 390,
    [theme.breakpoints.down('sm')]: {
      minWidth: '100%',
      minHeight: 390,
    }
  },
  journalLoading: {
    gridArea: 'journalList',
    margin: '8px auto',
  },
  overlay: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    zIndex: 100,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    background: theme.palette.contrastInverse.disabled,
    backdropFilter: 'blur(3px)',
  },
}), { name: displayName })

const getContextValue = shallowEqualsMemoizer(pick([
  'activeHarvest',
  'activeRoom',
  'activeTypes',
  'activeUser',
  'allowedTypes',
  'selectedDate',
  'topItem'
]), { depth: 2 })

// eslint-disable-next-line react/require-default-props
export const JournalComponent = props => {
  const { classes, calendar, journal, ...passthruProps } = props
  const [rect, ref] = useClientRect(journal?.items?.length)
  const journalState = useJournalState(props, rect?.height, logger)
  const { infiniteLoading, loaded, loading } = journalState
  const showOverlay = loaded && loading && !infiniteLoading

  return (
    <JournalContext.Provider value={getContextValue(journalState)}>
      <div className={classNames(classes.container, !calendar && classes.noCalendar)}>
        {showOverlay ? (
          <div className={classes.overlay}><CircularProgress color="primary" /></div>
        ) : null}
        {calendar ? (
          <div className={classes.calendarContainer}>
            <Calendar {...calendarStatePicker(journalState)} {...calendarPropsPicker(props)} />
          </div>
        ) : null}
        <div className={classes.filters}>
          <Filters {...getFilterProps(props, journalState)} />
        </div>
        <div className={classes.journal} ref={ref}>
          {!loaded ? <CircularProgress color="primary" className={classes.journalLoading} /> : (
            <List {...passthruProps} {...omitNonListState(journalState)} />
          )}
        </div>
      </div>
    </JournalContext.Provider>
  )
}
JournalComponent.propTypes = {
  calendar: PropTypes.bool,
  harvestDates: PropTypes.bool,
  journal: PropTypes.shape({
    items: PropTypes.arrayOf(PropTypes.shape({
      uuid: PropTypes.string,
      timestamp: PropTypes.string
    })),
  }).isRequired,
  // scoping props
  activeHarvest: PropTypes.oneOfType([PropTypes.symbol, idAndNameShape]),
  activeRoom: PropTypes.oneOfType([PropTypes.symbol, idAndNameShape]),
  activeUser: PropTypes.oneOfType([PropTypes.symbol, idAndNameShape]),
}
JournalComponent.defaultProps = {
  calendar: false,
  harvestDates: false,
  activeHarvest: SCOPING_PROP_DEFAULT_VALUE,
  activeRoom: SCOPING_PROP_DEFAULT_VALUE,
  activeUser: SCOPING_PROP_DEFAULT_VALUE,
}

const Wrapped = React.memo(({ hidden = false, ...props }) => {
  const connectedProps = useConnect(
    'selectDevices',
    'selectJournal',
    'selectMe',
    'selectUsers',
    'selectRooms',
    'selectZones',
    'doJournalFetch'
  )
  const classes = useStyles(props)

  return hidden ? null : <JournalComponent {...connectedProps} {...props} classes={classes} />
})

Wrapped.displayName = `Wrapped(${displayName})`

export default Wrapped
